import React from 'react';

import { SvgIcon } from '@mui/material';
import { IconColors } from 'src/components/icons/icon-utils';

export enum PulseIconVariant {
  LoadingPrimary,
  LoadingSecondary,
  Error,
  Success,
  WarnYellow,
  WarnAmber,
}

interface PulseIconProps {
  variant: PulseIconVariant;
  animate?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * PulseIcon is a pulsing icon that is used to indicate that the AI is thinking.
 */
export function PulseIcon({
  animate = true,
  className,
  style,
  variant,
}: PulseIconProps): JSX.Element {
  switch (variant) {
    case PulseIconVariant.LoadingPrimary:
    case PulseIconVariant.LoadingSecondary: {
      const fill =
        variant === PulseIconVariant.LoadingPrimary ? IconColors.Primary : IconColors.DarkBlue;

      return (
        <SvgIcon className={className} style={style} sx={{ fontSize: 18 }} viewBox="0 0 18 18">
          <circle cx="9" cy="9" fill={fill} fillOpacity="0.25" r="6">
            {animate && (
              <animate
                attributeName="fill-opacity"
                dur="1.5s"
                keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"
                keyTimes="0; 0.5; 1"
                repeatCount="indefinite"
                values="0.25;0;0.25"
              />
            )}
          </circle>
          <circle cx="9" cy="9" fill={fill} fillOpacity="0.25" opacity="0.2" r="8">
            {animate && (
              <animate
                attributeName="fill-opacity"
                dur="1.5s"
                keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"
                keyTimes="0; 0.5; 1"
                repeatCount="indefinite"
                values="0.25;0;0.25"
              />
            )}
          </circle>
          <circle cx="9" cy="9" fill={fill} r="3" />
        </SvgIcon>
      );
    }
    case PulseIconVariant.Error:
      return (
        <SvgIcon className={className} sx={{ fontSize: 18 }} viewBox="0 0 18 18">
          <circle cx="9" cy="9" fill={IconColors.Error} fillOpacity="0.25" r="6" />
          <circle cx="9" cy="9" fill={IconColors.Error} fillOpacity="0.25" opacity="0.2" r="8" />
          <circle cx="9" cy="9" fill={IconColors.Error} r="3" />
        </SvgIcon>
      );
    case PulseIconVariant.Success:
      return (
        <SvgIcon className={className} sx={{ fontSize: 18 }} viewBox="0 0 18 18">
          <circle cx="9" cy="9" fill={IconColors.Green} fillOpacity="0.25" r="6">
            {animate && (
              <animate
                attributeName="fill-opacity"
                dur="1.5s"
                keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"
                keyTimes="0; 0.5; 1"
                repeatCount="indefinite"
                values="0.25;0;0.25"
              />
            )}
          </circle>
          <circle cx="9" cy="9" fill={IconColors.Green} fillOpacity="0.25" opacity="0.2" r="8">
            {animate && (
              <animate
                attributeName="fill-opacity"
                dur="1.5s"
                keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"
                keyTimes="0; 0.5; 1"
                repeatCount="indefinite"
                values="0.25;0;0.25"
              />
            )}
          </circle>
          <circle cx="9" cy="9" fill={IconColors.Green} r="3" />
        </SvgIcon>
      );
    case PulseIconVariant.WarnYellow:
      return (
        <SvgIcon className={className} sx={{ fontSize: 18 }} viewBox="0 0 18 18">
          <circle cx="9" cy="9.5" fill={IconColors.WarnYellow} fillOpacity="0.25" r="6" />
          <circle
            cx="9"
            cy="9.5"
            fill={IconColors.WarnYellow}
            fillOpacity="0.25"
            opacity="0.2"
            r="8"
          />
          <circle cx="9" cy="9.5" fill={IconColors.WarnYellow} r="3" />
        </SvgIcon>
      );
    case PulseIconVariant.WarnAmber:
      return (
        <SvgIcon className={className} sx={{ fontSize: 18 }} viewBox="0 0 18 18">
          <circle cx="9" cy="9.5" fill={IconColors.WarnAmber} fillOpacity="0.25" r="6" />
          <circle
            cx="9"
            cy="9.5"
            fill={IconColors.WarnAmber}
            fillOpacity="0.25"
            opacity="0.2"
            r="8"
          />
          <circle cx="9" cy="9.5" fill={IconColors.WarnAmber} r="3" />
        </SvgIcon>
      );
  }
}
